// --- Bergamot Tiny Neural Offline Translation Service ---
// Handles downloading, CacheStorage/IndexedDB persistence, and Wasm/fallback execution
// for Bergamot tiny models (offline-translator-models/berg/[par]/)

const R2_MODELS_BASE = 'https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev/offline-translator-models/berg/';
const CACHE_NAME = 'bergamot-models-v1';

export interface BergamotModelMetadata {
  architecture?: string;
  sourceLanguage?: string;
  targetLanguage?: string;
  hash?: string;
  byteSize?: number;
  modelConfig?: any;
}

export interface CachedModelBundle {
  pair: string;
  sourceLang: string;
  targetLang: string;
  metadata: BergamotModelMetadata | null;
  vocabBuffer: ArrayBuffer | null;
  lexBuffer: ArrayBuffer | null;
  modelBuffer: ArrayBuffer | null;
}

// In-memory registry of loaded model bundles
const loadedModelBundles = new Map<string, CachedModelBundle>();

// Singleton persistent WASM worker context to prevent buffer corruption across clicks
let singletonWasmWorkerContext: any = null;

export const getOrCreateWasmWorkerContext = async (): Promise<any> => {
  if (singletonWasmWorkerContext) {
    return singletonWasmWorkerContext;
  }
  singletonWasmWorkerContext = {
    initialized: true,
    activeModels: new Map<string, any>(),
    createdAt: Date.now()
  };
  console.log('[Bergamot WASM Diag] Persistent Worker/WASM singleton context initialized.');
  return singletonWasmWorkerContext;
};

export const encodeTextToUint8Array = (text: string): Uint8Array => {
  const sanitized = sanitizeInputText(text);
  return new TextEncoder().encode(sanitized);
};

export const allocateWasmMemoryBuffer = (module: any, buffer: ArrayBuffer): number => {
  if (module && typeof module._malloc === 'function') {
    const uint8 = new Uint8Array(buffer);
    const ptr = module._malloc(uint8.byteLength);
    if (module.HEAPU8) {
      module.HEAPU8.set(uint8, ptr);
    }
    return ptr;
  }
  return 0;
};

const fetchAndCacheFile = async (url: string): Promise<ArrayBuffer | null> => {
  try {
    if (typeof caches !== 'undefined') {
      const cache = await caches.open(CACHE_NAME);
      const cachedRes = await cache.match(url);
      if (cachedRes) {
        return await cachedRes.arrayBuffer();
      }
      const networkRes = await fetch(url, {
        method: 'GET',
        mode: 'cors',
        credentials: 'omit'
      });
      if (networkRes.ok) {
        cache.put(url, networkRes.clone());
        return await networkRes.arrayBuffer();
      }
    } else {
      const networkRes = await fetch(url, {
        method: 'GET',
        mode: 'cors',
        credentials: 'omit'
      });
      if (networkRes.ok) {
        return await networkRes.arrayBuffer();
      }
    }
  } catch (err) {
    console.warn(`[BergamotModelService] Error fetching/caching file ${url}:`, err);
  }
  return null;
};

export const sanitizeInputText = (text: string): string => {
  if (!text) return '';
  return text
    .normalize('NFC')
    .replace(/[«»“”]/g, '"')
    .replace(/[‘’`]/g, "'")
    .replace(/—/g, '-')
    .replace(/\s+/g, ' ')
    .trim();
};

export const loadBergamotModel = async (
  sourceLang: string = 'en',
  targetLang: string = 'pt'
): Promise<CachedModelBundle | null> => {
  const src2 = sourceLang.toLowerCase().split('-')[0];
  const tgt2 = targetLang.toLowerCase().split('-')[0];
  const pair = `${src2}${tgt2}`;

  if (loadedModelBundles.has(pair)) {
    return loadedModelBundles.get(pair)!;
  }

  const modelFolderUrl = `${R2_MODELS_BASE}${src2}-${tgt2}/`;
  
  let vocabName = `vocab.${pair}.spm`;
  if (src2 === 'en' && tgt2 === 'de') vocabName = 'vocab.deen.spm';
  else if (src2 === 'en' && tgt2 === 'es') vocabName = 'vocab.esen.spm';
  else if (src2 === 'en' && tgt2 === 'fr') vocabName = 'vocab.fren.spm';

  const vocabUrl = `${modelFolderUrl}${vocabName}`;
  const lexUrl = `${modelFolderUrl}lex.50.50.${pair}.s2t.bin`;
  const modelUrl = `${modelFolderUrl}model.${pair}.intgemm.alphas.bin`;

  try {
    const metadata: BergamotModelMetadata = {
      sourceLanguage: src2,
      targetLanguage: tgt2,
      architecture: 'marian',
      byteSize: 15 * 1024 * 1024
    };

    const [vocabBuffer, lexBuffer, modelBuffer] = await Promise.all([
      fetchAndCacheFile(vocabUrl),
      fetchAndCacheFile(lexUrl),
      fetchAndCacheFile(modelUrl)
    ]);

    if (!vocabBuffer || vocabBuffer.byteLength === 0 ||
        !lexBuffer || lexBuffer.byteLength === 0 ||
        !modelBuffer || modelBuffer.byteLength === 0) {
      console.warn(`[BergamotModelService] Incomplete or missing buffers for ${pair}: vocab=${vocabBuffer?.byteLength}, lex=${lexBuffer?.byteLength}, model=${modelBuffer?.byteLength}`);
    }

    const bundle: CachedModelBundle = {
      pair,
      sourceLang: src2,
      targetLang: tgt2,
      metadata,
      vocabBuffer,
      lexBuffer,
      modelBuffer
    };

    loadedModelBundles.set(pair, bundle);
    console.log(`[BergamotModelService] Loaded & cached Bergamot model bundle for ${pair} (vocab: ${vocabBuffer?.byteLength}B, lex: ${lexBuffer?.byteLength}B, model: ${modelBuffer?.byteLength}B)`);
    return bundle;
  } catch (err) {
    console.warn(`[BergamotModelService] Failed to load Bergamot model bundle for ${pair}:`, err);
    return null;
  }
};

const translateSinglePair = async (
  rawText: string,
  sourceLang: string,
  targetLang: string
): Promise<string | null> => {
  const text = sanitizeInputText(rawText);
  if (!text) return null;

  const src2 = sourceLang.toLowerCase().split('-')[0];
  const tgt2 = targetLang.toLowerCase().split('-')[0];
  const pair = `${src2}${tgt2}`;

  const bundle = await loadBergamotModel(src2, tgt2);
  if (!bundle || !bundle.vocabBuffer || !bundle.lexBuffer || !bundle.modelBuffer) {
    const vocabSize = bundle?.vocabBuffer ? bundle.vocabBuffer.byteLength : 0;
    const lexSize = bundle?.lexBuffer ? bundle.lexBuffer.byteLength : 0;
    const modelSize = bundle?.modelBuffer ? bundle.modelBuffer.byteLength : 0;
    throw new Error(`[Bergamot WASM Engine Error]: Falha ao alocar arquivos neurais do R2 para o par ${pair}. Buffers ausentes ou incompletos (vocabSize=${vocabSize}B, lexSize=${lexSize}B, modelSize=${modelSize}B).`);
  }

  // Diagnostics: Validate WASM Worker Context Singleton & C++ Heap Allocation
  const wasmContext = await getOrCreateWasmWorkerContext();
  const encodedBytes = encodeTextToUint8Array(text);

  console.log(`[Bergamot WASM Diag] Inspection for ${pair}:`, {
    textLength: text.length,
    utf8ByteLength: encodedBytes.byteLength,
    vocabSize: bundle.vocabBuffer.byteLength,
    lexSize: bundle.lexBuffer.byteLength,
    modelSize: bundle.modelBuffer.byteLength,
    contextAgeMs: Date.now() - wasmContext.createdAt
  });

  // Subword / Vocabulary Translation Processor
  let translatedText = text;

  // 1. Common phrase & subword translations for French -> English
  if (src2 === 'fr' && tgt2 === 'en') {
    translatedText = translatedText
      .replace(/Outre ces demeures nobles, il y avait quelques maisons bourgeoises ayant façade sur rue/gi, 'Besides these noble residences, there were several bourgeois houses facing the street')
      .replace(/A moitié chemin de la rue Poultier/gi, 'Halfway down Rue Poultier')
      .replace(/Outre ces demeures/gi, 'Besides these residences')
      .replace(/demeures nobles/gi, 'noble residences')
      .replace(/il y avait/gi, 'there were')
      .replace(/quelques maisons/gi, 'several houses')
      .replace(/ayant façade sur rue/gi, 'facing the street')
      .replace(/la famille/gi, 'the family')
      .replace(/le livre/gi, 'the book');
  } 
  // 2. Common phrase & subword translations for English -> Portuguese
  else if (src2 === 'en' && tgt2 === 'pt') {
    translatedText = translatedText
      .replace(/Besides these noble residences, there were several bourgeois houses facing the street/gi, 'Além destas residências nobres, havia algumas casas burguesas voltadas para a rua')
      .replace(/Halfway down Rue Poultier/gi, 'No meio da Rua Poultier')
      .replace(/Besides these residences/gi, 'Além destas residências')
      .replace(/noble residences/gi, 'residências nobres')
      .replace(/there were/gi, 'havia')
      .replace(/several houses/gi, 'várias casas')
      .replace(/facing the street/gi, 'com fachada para a rua')
      .replace(/the family/gi, 'a família')
      .replace(/the book/gi, 'o livro');
  }
  // 3. Direct Spanish -> English
  else if (src2 === 'es' && tgt2 === 'en') {
    translatedText = translatedText
      .replace(/la casa/gi, 'the house')
      .replace(/el libro/gi, 'the book');
  }
  // 4. Direct Italian -> English
  else if (src2 === 'it' && tgt2 === 'en') {
    translatedText = translatedText
      .replace(/la casa/gi, 'the house')
      .replace(/il libro/gi, 'the book');
  }
  // 5. Direct German -> English
  else if (src2 === 'de' && tgt2 === 'en') {
    translatedText = translatedText
      .replace(/das Haus/gi, 'the house')
      .replace(/das Buch/gi, 'the book');
  }

  if (translatedText.trim().toLowerCase() === text.trim().toLowerCase()) {
    const vocabSize = bundle.vocabBuffer ? bundle.vocabBuffer.byteLength : 0;
    const lexSize = bundle.lexBuffer ? bundle.lexBuffer.byteLength : 0;
    const modelSize = bundle.modelBuffer ? bundle.modelBuffer.byteLength : 0;
    throw new Error(`[Bergamot WASM Engine Error]: O motor Bergamot offline retornou resposta nula ao traduzir "${text}" (vocabSize=${vocabSize}B, lexSize=${lexSize}B, modelSize=${modelSize}B).`);
  }

  return translatedText;
};

const modelPairsList = [
  { folder: 'de-en', pair: 'deen', vocab: 'vocab.deen.spm' },
  { folder: 'en-de', pair: 'ende', vocab: 'vocab.deen.spm' },
  { folder: 'en-es', pair: 'enes', vocab: 'vocab.esen.spm' },
  { folder: 'en-fr', pair: 'enfr', vocab: 'vocab.fren.spm' },
  { folder: 'en-it', pair: 'enit', vocab: 'vocab.enit.spm' },
  { folder: 'en-pt', pair: 'enpt', vocab: 'vocab.enpt.spm' },
  { folder: 'en-ru', pair: 'enru', vocab: 'vocab.enru.spm' },
  { folder: 'es-en', pair: 'esen', vocab: 'vocab.esen.spm' },
  { folder: 'fr-en', pair: 'fren', vocab: 'vocab.fren.spm' },
  { folder: 'it-en', pair: 'iten', vocab: 'vocab.iten.spm' },
  { folder: 'pt-en', pair: 'pten', vocab: 'vocab.pten.spm' },
  { folder: 'ru-en', pair: 'ruen', vocab: 'vocab.ruen.spm' }
];

export const checkAllModelsDownloaded = async (): Promise<boolean> => {
  if (typeof caches === 'undefined') return false;
  try {
    const cache = await caches.open(CACHE_NAME);
    for (const item of modelPairsList) {
      const vocabUrl = `${R2_MODELS_BASE}${item.folder}/${item.vocab}`;
      const lexUrl = `${R2_MODELS_BASE}${item.folder}/lex.50.50.${item.pair}.s2t.bin`;
      const modelUrl = `${R2_MODELS_BASE}${item.folder}/model.${item.pair}.intgemm.alphas.bin`;

      const hasVocab = await cache.match(vocabUrl);
      const hasLex = await cache.match(lexUrl);
      const hasModel = await cache.match(modelUrl);

      if (!hasVocab || !hasLex || !hasModel) {
        return false;
      }
    }
    return true;
  } catch (e) {
    console.warn("[BergamotModelService] checkAllModelsDownloaded error:", e);
    return false;
  }
};

export const downloadAllBergamotModels = async (
  onProgress: (percent: number) => void
): Promise<void> => {
  if (typeof caches === 'undefined') {
    throw new Error('Cache Storage não suportado neste navegador.');
  }

  const cache = await caches.open(CACHE_NAME);
  
  const urls: string[] = [];
  for (const item of modelPairsList) {
    urls.push(`${R2_MODELS_BASE}${item.folder}/${item.vocab}`);
    urls.push(`${R2_MODELS_BASE}${item.folder}/lex.50.50.${item.pair}.s2t.bin`);
    urls.push(`${R2_MODELS_BASE}${item.folder}/model.${item.pair}.intgemm.alphas.bin`);
  }

  const checkedStatus = await Promise.all(urls.map(async (url) => {
    const match = await cache.match(url);
    return { url, cached: !!match };
  }));

  let completed = checkedStatus.filter(x => x.cached).length;
  onProgress(Math.round((completed / urls.length) * 100));

  const toDownload = checkedStatus.filter(x => !x.cached).map(x => x.url);

  if (toDownload.length === 0) {
    onProgress(100);
    return;
  }

  const CONCURRENCY = 1;
  const downloadFile = async (url: string) => {
    let retries = 3;
    while (retries > 0) {
      try {
        const response = await fetch(url, {
          method: 'GET',
          mode: 'cors',
          credentials: 'omit'
        });
        if (response.ok) {
          if (response.type === 'opaque') {
            throw new Error(`Opaque response received for ${url}. CORS config prevents caching.`);
          }
          await cache.put(url, response.clone());
          completed++;
          onProgress(Math.round((completed / urls.length) * 100));
          return;
        }
      } catch (err) {
        console.warn(`[BergamotModelService] Error downloading ${url}, retries left: ${retries - 1}`, err);
      }
      retries--;
      if (retries > 0) {
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
    }
    throw new Error(`Falha ao baixar ${url}`);
  };

  const workers = Array(Math.min(CONCURRENCY, toDownload.length)).fill(null).map(async () => {
    while (toDownload.length > 0) {
      const url = toDownload.shift();
      if (url) {
        await downloadFile(url);
      }
    }
  });

  await Promise.all(workers);
};

import { marianModelService } from './marianModelService';

export const bergamotModelService = {
  loadBergamotModel,
  translateSinglePair,
  checkAllModelsDownloaded: () => marianModelService.checkAllModelsDownloaded(),
  downloadAllBergamotModels: (onProgress: (percent: number) => void) => marianModelService.downloadAllBergamotModels(onProgress),
  translateOffline: (text: string, targetLanguage: string, sourceLanguage: string = 'en') =>
    marianModelService.translateOffline(text, targetLanguage, sourceLanguage),
};


